import { useAuthStatus } from '@/hooks';
import { useAppStore } from '@/stores/app';
import { Org } from '@/types';
import Settings from '@components/Settings';
import { Avatar } from '@nautilusbio/nb-design-system/Avatar';
import { Button } from '@nautilusbio/nb-design-system/Button';
import type { ButtonProps } from '@nautilusbio/nb-design-system/Button';
import Group from '@nautilusbio/nb-design-system/Group';
import { Popover } from '@nautilusbio/nb-design-system/Popover';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import type { StackProps } from '@nautilusbio/nb-design-system/Stack';
import clsx from 'clsx';
import { LogOut } from 'lucide-react';
import React, { FC, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import styles from '../styles.module.css';

import DevControls from './DevControls';
import OrgSelector from './OrgSelector';

const defaultButtonProps: Omit<ButtonProps, 'data-testid'> = {
  fullWidth: true,
  size: 'sm',
};

const UserMenu: FC<{
  selectedOrg?: Org;
  onLogout: () => void;
  isSidebarCollapsed: boolean;
  isLoggingOut?: boolean;
}> = ({ selectedOrg, onLogout, isSidebarCollapsed, isLoggingOut = false }) => {
  const { t } = useTranslation('sidebar');
  const { user } = useAuthStatus();

  const { isSidebarMenuOpen, setIsSidebarMenuOpen, setIsOrgSelectorOpen } = useAppStore(
    useShallow(state => ({
      isSidebarMenuOpen: state.isSidebarMenuOpen,
      setIsSidebarMenuOpen: state.setIsSidebarMenuOpen,
      setIsOrgSelectorOpen: state.setIsOrgSelectorOpen,
    })),
  );

  const footerControls = useMemo(
    () => (
      <Stack
        className={clsx(
          isSidebarMenuOpen && styles.userMenuControlsFullSidebar,
          isSidebarCollapsed && styles.userMenuControlsCollapsedSidebar,
        )}
        fullWidth
        gap={isSidebarCollapsed ? 2 : undefined}
      >
        {selectedOrg && isSidebarCollapsed && <OrgSelector selectedOrg={selectedOrg} />}

        <Group alignItems="center" orientation="vertical">
          <Settings id={'settings'} title={t('settings', { ns: 'general' })} />

          {user && (
            <Button
              {...defaultButtonProps}
              aria-label="Logout"
              data-testid={isSidebarCollapsed ? 'logout-button-collapsed' : 'logout-button-expanded'}
              isDisabled={isLoggingOut}
              onPress={onLogout}
              visual="danger"
            >
              <LogOut className={styles.logoutIcon} size={16} />
              {isLoggingOut ? t('logging_out', { ns: 'login' }) : t('buttons.logout', { ns: 'login' })}
            </Button>
          )}
        </Group>
      </Stack>
    ),
    [isSidebarCollapsed, isSidebarMenuOpen, selectedOrg, user, onLogout, t, isLoggingOut],
  );

  const footerContent: Partial<StackProps> & { children: ReactNode } = useMemo(
    () => ({
      children: (
        <>
          <DevControls />
          {footerControls}
        </>
      ),
    }),
    [footerControls],
  );

  const avatarProps = useMemo(
    () => ({
      children: user?.full_name,
    }),
    [user?.full_name],
  );

  const optionsToggleHandler = () => {
    setIsSidebarMenuOpen(!isSidebarMenuOpen);
    setIsOrgSelectorOpen(false);
  };

  return (
    <Popover
      isOpen={isSidebarMenuOpen}
      padding={0}
      content={
        <Stack alignItems="start" className={styles.popoverMenu} gap={3} orientation="vertical">
          <Stack
            {...footerContent}
            className={clsx(styles.popoverContent, styles.userMenuControls)}
            orientation="vertical"
          />
        </Stack>
      }
      onClickOutside={() => setIsSidebarMenuOpen(false)}
      data-testid="sidebar-user-menu-popover"
      positions={['top']}
      styled={false}
      showArrow
    >
      <Button data-testid="sidebar-avatar-button" onPress={optionsToggleHandler} visual="unstyled">
        <Avatar {...avatarProps} />
      </Button>
    </Popover>
  );
};

export default UserMenu;
